/**
 * AIFileUploadV2 FileDetail page — AC-3.3
 *
 * Branches on detail.status:
 *   - processing / extracting   → V15a (Processing screen)
 *   - completed                 → V15 (Review screen)
 *   - rejected                  → Rejection screen
 *   - other                     → fallback to Hub
 */
import React, { JSX, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Divider from '@mui/material/Divider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { useRenterFileDetail } from 'src/hooks/aiFileUploadV2/useAIFileDetail';
import { patchMatch, deleteAIFile } from 'src/services/aiFileUploadV2';
import { useAIFileUploadV2ContextOptional } from 'src/contexts/AIFileUploadV2Context';

// Safe fallback — matches safety.py CONFIDENCE_THRESHOLD
import { formatMatchValue } from './utils/formatMatchValue';
import { DEFAULT_CONFIDENCE_THRESHOLD } from './constants';
import type {
  ExtractionMatch,
  FileProcessingStep,
} from 'src/types/aiFileUploadV2';
import DocumentPreviewBox from './components/DocumentPreviewBox';
import ProcessingChecklist from './components/ProcessingChecklist';
import InlineEditField from './components/InlineEditField';
import ClassifyWidget from './components/ClassifyWidget';
import MemberConfirmationWidget from './components/MemberConfirmationWidget';
import { REJECTION_COPY } from './constants';
import { errorCopyBody, renderErrorCopy } from './utils/renderErrorCopy';
import { GENERIC_ERROR_COPY } from './errorMessages';

// ─── value formatter ────────────────────────────────────────────────────────

// ─── module-scope sx ────────────────────────────────────────────────────────

// ─── module-scope sx ────────────────────────────────────────────────────────

const sx = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    bgcolor: 'background.paper',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    px: 2,
    py: 1.5,
    bgcolor: 'grey.900',
  },
  backBtn: {
    color: 'grey.400',
    textTransform: 'none' as const,
    fontSize: 14,
    fontWeight: 500,
    px: 0,
    minWidth: 0,
    '&:hover': { color: 'grey.100', backgroundColor: 'transparent' },
  },
  body: {
    flex: 1,
    px: 2,
    pt: 3,
    pb: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  },
  processingTitle: {
    fontSize: 20,
    fontWeight: 600,
  },
  processingHint: {
    fontSize: 14,
    color: 'text.secondary',
    mt: 1,
  },
  // Section label — small, secondary, less visual weight than content
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: 'text.secondary',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    mb: 1,
  },
  matchCard: {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1.5,
    p: 0.75,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    // Allow the card to shrink below its content's intrinsic width inside
    // its parent flex column. Without this, a long unbreakable token in a
    // child Typography forces the card to overflow horizontally on mobile.
    minWidth: 0,
  },
  // Question name — small, secondary
  matchQuestion: {
    fontSize: 11,
    color: 'text.secondary',
    // Long property names with no whitespace must still wrap. We use
    // break-word (not break-all/anywhere) so naturally breakable tokens
    // like dates ("04/06/2026") stay intact.
    overflowWrap: 'break-word' as const,
    minWidth: 0,
  },
  // Value — large, primary, prominent
  matchValue: {
    fontSize: 14,
    fontWeight: 600,
    color: 'text.primary',
    // Long values like IDs, URLs, or filenames are single unbreakable
    // tokens — without break-word they bleed out of the card on mobile.
    // Intentionally NOT 'anywhere'/'break-all' which would split readable
    // tokens like "04/06/2026" mid-string.
    overflowWrap: 'break-word' as const,
  },
  // "Used to answer" caption shown under DOCUMENT TYPE for file-matches.
  // Read-only and visually small to make clear the file IS the answer —
  // there is no value to edit and no Edit button.
  fileMatchCaption: {
    fontSize: 13,
    color: 'text.secondary',
    fontStyle: 'italic' as const,
    mt: 0.5,
    overflowWrap: 'break-word' as const,
  },
  matchActions: {
    display: 'flex',
    gap: 1,
    mt: 0.5,
  },
  failedStepsSection: {
    mt: 1,
  },
  failedStepItem: {
    fontSize: 13,
    color: 'text.secondary',
    py: 0.5,
  },
  // Error alert — shown at the very top of the body for failed files so
  // the user immediately understands the file is unusable. Uses MUI Alert
  // severity="info" so the failure reads in the info blue, not alarm red.
  errorAlert: {
    borderRadius: 1.5,
    fontSize: 13,
    lineHeight: 1.5,
    alignItems: 'flex-start',
  },
  rejectionBody: {
    flex: 1,
    px: 2,
    pt: 4,
    pb: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  rejectionTitle: {
    fontSize: 20,
    fontWeight: 600,
  },
  rejectionCopy: {
    fontSize: 15,
    color: 'text.secondary',
  },
};

// ─── MatchCard ───────────────────────────────────────────────────────────────

interface MatchCardProps {
  match: ExtractionMatch;
  needsConfirm: boolean;
  onConfirm: (matchId: number) => Promise<void>;
  onEdit: (matchId: number, value: string) => Promise<void>;
}

function MatchCard({
  match,
  needsConfirm,
  onConfirm,
  onEdit,
}: MatchCardProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  // Local override so the new value shows immediately after save without waiting for a refetch
  const [localValue, setLocalValue] = useState<string | null>(null);

  const handleConfirm = useCallback(async () => {
    setBusy(true);
    try {
      await onConfirm(match.id);
    } finally {
      setBusy(false);
    }
  }, [match.id, onConfirm]);

  const handleEditCommit = useCallback(
    async (value: string) => {
      if (value.trim() !== '') {
        setBusy(true);
        try {
          await onEdit(match.id, value);
          setLocalValue(value);
        } finally {
          setBusy(false);
        }
      }
      setEditing(false);
    },
    [match.id, onEdit]
  );

  const rawValue =
    localValue ??
    match.user_edited_value ??
    match.proposed_answer_text ??
    match.extracted_value;

  const displayValue = formatMatchValue(
    rawValue,
    match.value_type ?? match.qit?.question_type
  );

  return (
    <Box sx={sx.matchCard} data-testid="match-card">
      {/* Question name + Edit button side by side */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          minWidth: 0,
        }}
      >
        <Typography sx={sx.matchQuestion}>{match.question_name}</Typography>
        {!needsConfirm && !editing && (
          <Button
            size="small"
            onClick={() => setEditing(true)}
            disabled={busy}
            data-testid="edit-btn"
            sx={{
              minWidth: 0,
              p: '4px',
              flex: '0 0 auto',
              color: 'text.secondary',
            }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </Button>
        )}
      </Box>

      {editing ? (
        <InlineEditField
          initialValue={rawValue}
          onCommit={handleEditCommit}
          onCancel={() => setEditing(false)}
          saving={busy}
          disabled={false}
        />
      ) : (
        <Typography sx={sx.matchValue} data-testid="match-value">
          {displayValue}
        </Typography>
      )}

      {/* Confirm + Edit actions — only for low-confidence unconfirmed matches */}
      {needsConfirm && !match.user_confirmed && !editing && (
        <Box sx={sx.matchActions}>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirm}
            disabled={busy}
            data-testid="confirm-btn"
          >
            <span>Confirm</span>
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setEditing(true)}
            disabled={busy}
            data-testid="edit-btn"
          >
            <span>Edit</span>
          </Button>
        </Box>
      )}

      {match.user_confirmed && needsConfirm && (
        <Chip
          label="Confirmed"
          size="small"
          color="success"
          data-testid="confirmed-chip"
        />
      )}
    </Box>
  );
}

// ─── FileDetail page ─────────────────────────────────────────────────────────

interface FileDetailProps {
  /** Override URL param — used when embedded in ClassifyFunnel */
  fileId?: number;
  /** Classify funnel counter props — passed through to ClassifyWidget */
  fileIndex?: number;
  totalFiles?: number;
  onClassified?: (fileId: number) => void;
  onRemove?: (fileId: number) => Promise<void>;
  onBack?: () => void;
  onNotFound?: () => void;
}

const FileDetail = ({
  fileId: fileIdProp,
  fileIndex,
  totalFiles,
  onClassified,
  onRemove,
  onBack,
  onNotFound,
}: FileDetailProps = {}): JSX.Element | null => {
  const { fileId: fileIdParam } = useParams<{ fileId: string }>();
  const navigate = useNavigate();
  const ctx = useAIFileUploadV2ContextOptional();
  const { confidenceThreshold } = ctx ?? {
    confidenceThreshold: DEFAULT_CONFIDENCE_THRESHOLD,
  };
  const fileId = fileIdProp ?? (fileIdParam ? Number(fileIdParam) : null);
  const { detail, loading, error, refetch } = useRenterFileDetail(fileId, {
    onNotFound,
  });

  // Live WS-updated file entry — has current_step and completed_steps updated in real time
  const liveFileData = fileId && ctx ? ctx.files.fileMap[fileId] : null;

  // Blocking failures are MOVED from fileMap into failedEntries by
  // applyWSPatch(deleteFromList=true) — the hub reads failedEntries for its
  // error banner, but this page previously only read fileMap, so a failure
  // arriving while the user watched the detail view left it stuck on the
  // "Reading your file" screen forever (the server row is auto-deleted, so
  // no refetch can rescue it). Prefer the failed entry when present.
  const liveFailedData =
    fileId && ctx ? ctx.files.failedEntries?.[fileId] ?? null : null;

  // If the file isn't in the WS fileMap yet (e.g. navigated directly to /files/110
  // while processing was already in flight), seed it from the REST detail so that
  // subsequent WS patch events have an entry to update.
  useEffect(() => {
    if (!detail || !ctx || !fileId) {
      return;
    }
    if (ctx.files.fileMap[fileId]) {
      return;
    } // already seeded
    ctx.files.applyWSPatch(
      fileId,
      {},
      {
        id: fileId,
        original_filename: detail.original_filename,
        status: detail.status,
        session_status: detail.session_status,
        current_step: detail.current_step,
        completed_steps: detail.completed_steps,
        total_steps: detail.total_steps,
        needs_manual_classification: detail.needs_manual_classification,
        is_duplicate: detail.is_duplicate,
        is_valid: detail.is_valid,
        match_count: detail.matches?.length ?? 0,
        mime_type: detail.mime_type,
        file_size_bytes: detail.file_size_bytes,
        page_count: detail.page_count,
        doc_category_key: detail.doc_category_key,
        doc_sub_type_key: detail.doc_sub_type_key,
        doc_category_display: detail.doc_category_display,
        doc_sub_type_display: detail.doc_sub_type_display,
        classification_confidence: detail.classification_confidence,
        classification_method: detail.classification_method,
        error_message: detail.error_message,
        error_step: detail.error_step,
        rejection_reason_category: detail.rejection_reason_category,
        created_at: detail.created_at,
        updated_at: detail.updated_at,
        member_confirmation: detail.extracted_data?.member_confirmation ?? null,
      }
    );
  }, [detail, ctx, fileId]);

  // Prefer live WS status over stale REST status so the processing checklist
  // and status transitions update in real time from WS events.
  const liveStatus = liveFileData?.status ?? (liveFailedData ? 'failed' : null);

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
      return;
    }
    navigate(-1);
  }, [navigate, onBack]);

  // Delete decision from MemberConfirmationWidget already discarded the
  // file server-side (resolve-member-confirmation, decision='delete') —
  // just navigate away, mirroring handleRemove's post-delete navigation
  // without issuing a second (redundant) delete call.
  const handleMemberConfirmationDeleted = useCallback((): void => {
    handleBack();
  }, [handleBack]);

  // Removal state — shown on both failed and rejected screens.
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Send DELETE /uploads/{id}/ directly, then navigate to the upload page.
  // Uses navigate('/renter/files', { replace: true }) so the back button
  // doesn't re-open a deleted file's detail page.
  // When onRemove is provided (ClassifyFunnel), delegate to it instead —
  // it handles its own navigation.
  const handleRemove = useCallback(async (): Promise<void> => {
    if (!fileId) {
      return;
    }
    if (onRemove) {
      await onRemove(fileId);
      return;
    }
    setRemoving(true);
    setRemoveError(null);
    try {
      await deleteAIFile(fileId);
      handleBack();
    } catch {
      setRemoveError('Could not remove this file. Please try again.');
      setRemoving(false);
    }
  }, [fileId, onRemove, handleBack]);

  // After classifying, refresh detail so status updates away from needs_classification
  const handleClassified = useCallback(
    (classifiedFileId: number) => {
      if (onClassified) {
        onClassified(classifiedFileId);
      }
      refetch();
    },
    [onClassified, refetch]
  );

  // Patch helpers — fileId is required as the first arg per service signature
  const handleConfirm = useCallback(
    async (matchId: number): Promise<void> => {
      if (!fileId) {
        return;
      }
      await patchMatch(fileId, matchId, { user_confirmed: true });
    },
    [fileId]
  );

  const handleEdit = useCallback(
    async (matchId: number, value: string): Promise<void> => {
      if (!fileId) {
        return;
      }
      await patchMatch(fileId, matchId, { value });
    },
    [fileId]
  );

  if (loading || (!detail && !error)) {
    return (
      <Box sx={sx.page} data-testid="file-detail-loading">
        {/* Header bar skeleton */}
        <Box sx={{ ...sx.header, minHeight: 48 }} />
        {/* Document preview area skeleton */}
        <Skeleton
          variant="rectangular"
          height={320}
          animation="wave"
          sx={{ bgcolor: 'grey.800' }}
        />
        {/* Body skeleton */}
        <Box sx={sx.body}>
          <Skeleton variant="text" height={32} width="60%" animation="wave" />
          <Skeleton variant="text" height={18} width="40%" animation="wave" />
          <Skeleton
            variant="rounded"
            height={88}
            animation="wave"
            sx={{ mt: 1 }}
          />
          <Skeleton variant="rounded" height={88} animation="wave" />
          <Skeleton variant="rounded" height={88} animation="wave" />
        </Box>
      </Box>
    );
  }

  if (!detail) {
    // error state — 404 is already handled by the hook (navigates away)
    return null;
  }

  const status = liveStatus ?? detail.status;

  // ── Rejected ──────────────────────────────────────────────────────────────

  if (status === 'rejected') {
    // Prefer the stored, backend-generated copy; fall back to the category copy.
    const rejectionCopy = detail.rejection_reason_category
      ? REJECTION_COPY[detail.rejection_reason_category] ??
        'We couldn\u2019t process this document.'
      : 'We couldn\u2019t process this document.';

    return (
      <Box sx={sx.page} data-testid="file-detail-rejected">
        <Box sx={sx.header}>
          <Button
            sx={sx.backBtn}
            onClick={handleBack}
            startIcon={<ArrowBackIcon sx={{ fontSize: '18px !important' }} />}
            disableRipple
            aria-label="Back to file uploads"
            data-testid="back-to-hub-btn"
          >
            <span>Back to file uploads</span>
          </Button>
        </Box>
        <DocumentPreviewBox
          filename={detail.original_filename}
          imageUrl={detail.first_page_image_url}
          processedFileUrl={detail.processed_file_url}
        />
        <Box sx={sx.body}>
          <Typography sx={sx.rejectionTitle}>
            We couldn&apos;t read this document
          </Typography>
          <Typography sx={sx.rejectionCopy} data-testid="rejection-copy">
            {/* Only the copy bodies — the first line of each copy is its header
                and is dropped. */}
            {renderErrorCopy(
              errorCopyBody(liveFailedData ?? detail) || rejectionCopy
            )}
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={handleRemove}
            disabled={removing}
            data-testid="remove-file-btn"
            startIcon={
              removing ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
          >
            <span>{removing ? 'Removing…' : 'Remove file'}</span>
          </Button>
          {removeError && (
            <Typography sx={{ fontSize: 13, color: 'info.main', mt: 0.5 }}>
              {removeError}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // ── Needs classification — prompt user to label the file ─────────────────

  if (status === 'needs_classification') {
    return (
      <ClassifyWidget
        fileId={detail.id}
        filename={detail.original_filename}
        filePreviewUrl={detail.first_page_image_url}
        processedFileUrl={detail.processed_file_url}
        fileIndex={fileIndex}
        totalFiles={totalFiles}
        onClassified={handleClassified}
        onRemove={onRemove}
        onBack={handleBack}
      />
    );
  }

  // ── Needs member confirmation — cross-member mismatch (renter-interview
  //    verify mode only) — three-way confirm/reject/delete prompt ─────────
  //    docs/plans/sorting-hat-cross-member-and-unification.md §1

  if (status === 'needs_member_confirmation') {
    const memberConfirmation =
      liveFileData?.member_confirmation ??
      detail.extracted_data?.member_confirmation ??
      null;

    // Defensive fallback — the backend always persists this payload before
    // setting the status (member_identification.py::_pause_confirmation),
    // so this should not happen. Avoid rendering a widget with nothing to
    // show rather than guessing at placeholder names.
    if (!memberConfirmation) {
      return (
        <Box sx={sx.page} data-testid="file-detail-member-confirmation-missing">
          <Box sx={sx.header}>
            <Button
              sx={sx.backBtn}
              onClick={handleBack}
              startIcon={<ArrowBackIcon sx={{ fontSize: '18px !important' }} />}
              disableRipple
              aria-label="Back to file uploads"
              data-testid="back-to-hub-btn"
            >
              <span>Back to file uploads</span>
            </Button>
          </Box>
          <Box sx={sx.body}>
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
              We couldn&apos;t load the details for this file. Please try again
              in a moment.
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <MemberConfirmationWidget
        fileId={detail.id}
        filename={detail.original_filename}
        filePreviewUrl={detail.first_page_image_url}
        processedFileUrl={detail.processed_file_url}
        confirmation={memberConfirmation}
        onDeleted={handleMemberConfirmationDeleted}
        onSubmitted={handleBack}
        onBack={handleBack}
      />
    );
  }

  // ── V15a — Processing ─────────────────────────────────────────────────────

  const isProcessing =
    status === 'processing' ||
    status === 'pending' ||
    status === 'extracting' ||
    status === 'classified';

  if (isProcessing) {
    return (
      <Box sx={sx.page} data-testid="file-detail-processing">
        <Box sx={sx.header}>
          <Button
            sx={sx.backBtn}
            onClick={handleBack}
            startIcon={<ArrowBackIcon sx={{ fontSize: '18px !important' }} />}
            disableRipple
            aria-label="Back to file uploads"
            data-testid="back-to-hub-btn"
          >
            <span>Back to file uploads</span>
          </Button>
        </Box>
        <DocumentPreviewBox
          filename={detail.original_filename}
          imageUrl={detail.first_page_image_url}
          processedFileUrl={detail.processed_file_url}
        />
        <Box sx={sx.body}>
          <Typography sx={sx.processingTitle}>Reading your file</Typography>
          <Chip
            label="Processing"
            size="small"
            color="warning"
            sx={{ alignSelf: 'flex-start' }}
          />
          <ProcessingChecklist
            liveSteps={detail.steps}
            currentStepName={liveFileData?.current_step ?? null}
            completedStepsCount={liveFileData?.completed_steps ?? 0}
          />
          <Typography sx={sx.processingHint}>
            This usually takes a few seconds. You can keep answering and
            we&apos;ll let you know when it&apos;s done.
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── V15 — Review (completed / failed) ────────────────────────────────────

  // The file-match (is_file_match=true) represents the RenterFile itself
  // fulfilling a file-type QIT — its "value" is the file, NOT the filename.
  // We surface it as a small read-only caption under DOCUMENT TYPE so the
  // user sees WHY this file is here, instead of mis-rendering it as an
  // editable text answer that shows the filename.
  const fileMatch =
    status !== 'failed'
      ? detail.matches.find((m) => m.is_file_match) ?? null
      : null;

  // For failed files, hide all matches — partial extractions from a failed
  // pipeline are never applied and showing them would mislead the user into
  // thinking the data was used.
  const activeMatches =
    status === 'failed'
      ? []
      : detail.matches.filter((m) => !m.user_excluded && !m.is_file_match);

  // Low confidence AND not yet confirmed → needs user confirmation
  const needsConfirmMatches = activeMatches.filter(
    (m) => m.confidence < confidenceThreshold && !m.user_confirmed
  );
  // Everything else — high confidence or already confirmed
  const filledInMatches = activeMatches.filter(
    (m) => m.confidence >= confidenceThreshold || m.user_confirmed
  );

  // Steps that didn't pass — exclude cost/eng-data steps (plan §8.4).
  // Only shown for completed files; failed files show the error alert instead.
  const nonPassingSteps =
    status !== 'failed'
      ? (detail.steps as FileProcessingStep[]).filter(
          (s) =>
            (s.status === 'failed' || s.status === 'skipped') &&
            !['score_confidence', 'assess_quality', 'finalize'].includes(
              s.step_name
            )
        )
      : [];

  return (
    <Box sx={sx.page} data-testid="file-detail-review">
      <Box sx={sx.header}>
        <Button
          sx={sx.backBtn}
          onClick={handleBack}
          startIcon={<ArrowBackIcon sx={{ fontSize: '18px !important' }} />}
          disableRipple
          aria-label="Back to file uploads"
          data-testid="back-to-hub-btn"
        >
          <span>Back to file uploads</span>
        </Button>
      </Box>
      <DocumentPreviewBox
        filename={detail.original_filename}
        imageUrl={detail.first_page_image_url}
        processedFileUrl={detail.processed_file_url}
      />
      <Box sx={sx.body}>
        {/* Error alert — first thing the user sees for failed files.
            Shown before document type and matches so the "unusable file"
            message is immediately obvious without any scrolling. */}
        {status === 'failed' && (
          <Alert severity="info" sx={sx.errorAlert} data-testid="failed-alert">
            <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>
              We couldn&apos;t process this document
            </Typography>
            <Typography
              sx={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-line' }}
            >
              {/* WS failure entries carry the error fields the stale REST
                  detail never received (the row was deleted server-side).
                  Only the copy bodies are shown (the header line is dropped). */}
              {renderErrorCopy(
                errorCopyBody(liveFailedData ?? detail) || GENERIC_ERROR_COPY
              )}
            </Typography>
          </Alert>
        )}

        {/* Document type — small label, prominent value.
            When this file fulfills a file-type QIT, also show a small
            "Used to answer" caption pointing at the QIT's question name.
            The file itself IS the answer here — there is nothing to edit. */}
        {(detail.doc_category_display || fileMatch) && (
          <Box>
            {detail.doc_category_display && (
              <>
                <Typography sx={sx.sectionTitle}>Document type</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {detail.doc_category_display}
                </Typography>
              </>
            )}
            {fileMatch && (
              <Typography
                sx={sx.fileMatchCaption}
                data-testid="file-match-caption"
              >
                Used to answer: {fileMatch.question_name}
              </Typography>
            )}
          </Box>
        )}

        {/* "We'd like you to confirm" — low confidence unconfirmed matches */}
        {needsConfirmMatches.length > 0 && (
          <Box>
            <Typography sx={sx.sectionTitle}>
              We&apos;d like you to confirm
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {needsConfirmMatches.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                  needsConfirm
                  onConfirm={handleConfirm}
                  onEdit={handleEdit}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* "What we filled in" — high confidence + confirmed matches */}
        {filledInMatches.length > 0 && (
          <Box>
            <Typography sx={sx.sectionTitle}>What we filled in</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {filledInMatches.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                  needsConfirm={false}
                  onConfirm={handleConfirm}
                  onEdit={handleEdit}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Remove file CTA — shown in all terminal states, disabled while
            processing so the user knows it exists but can't act mid-flight. */}
        <Box>
          <Button
            variant="outlined"
            color="error"
            onClick={handleRemove}
            disabled={removing || isProcessing}
            fullWidth
            data-testid="remove-file-btn"
            startIcon={
              removing ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
          >
            <span>{removing ? 'Removing…' : 'Remove file'}</span>
          </Button>
          {removeError && (
            <Typography sx={{ fontSize: 13, color: 'info.main', mt: 0.5 }}>
              {removeError}
            </Typography>
          )}
        </Box>

        {/* Non-failing step issues (skipped etc.) — only shown for
            completed files with steps that didn't fully pass. */}
        {nonPassingSteps.length > 0 && (
          <>
            <Divider />
            <Box sx={sx.failedStepsSection} data-testid="failed-steps-section">
              <Typography sx={sx.sectionTitle}>
                Steps that didn&apos;t pass
              </Typography>
              {nonPassingSteps.map((step) => (
                <Typography
                  key={step.step_name}
                  sx={sx.failedStepItem}
                  data-testid="failed-step-item"
                >
                  {step.display_name}
                </Typography>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default FileDetail;
